/**
 * AI ポイントの残量による操作の活性判定 (画面レイアウト_SCR-M-001.md §6.4.4、Issue #882)
 *
 * 残量 API (GET /api/matching/quota) の応答の `required_points` は、操作ごとの「1 回に必要な
 * ポイント」で、キーは CD-003 のコード値、値はサーバが判定と消費に使う定数そのもの
 * (PBI-002 §11.6)。画面は必要なポイントの数を持たず、キーだけを持って応答の値で判定する
 * (値は CD-003 で未確定〔Issue #876〕で、確定して変わってもサーバの応答に追随させるため)。
 */

/** `required_points` のキー (CD-003)。PBI-002 §11.6 の表と対応する */
export const QUOTA_OPERATION = {
    /** マッチングの実行 1 回 (BTN-M-002) */
    matchingScoring: 'MATCHING_SCORING',
    /** 求人票の取込 1 ファイル (IN-M-001・BTN-M-008) */
    jobPoolExtraction: 'JOB_POOL_EXTRACTION',
    // 推薦結果の PDF 出力 (BTN-M-003) は持たない。PDF 出力は AI を呼ばず、ポイントを消費しないため
    // 残量によらず押せる (§4.3 BTN-M-003 ② の廃止、Issue #1208)
};

/**
 * AI ポイント不足の案内 (§5.2 No.1・No.5。No.6 は Issue #1208 で廃止)。
 * どの箇所も同じ文言で、ポイントの数は入れない (数が変わるたびに文言が変わるのを避けるため)。
 */
export const INSUFFICIENT_POINTS_MESSAGE = 'AIポイントが不足しています。管理者に補充を依頼してください';

/**
 * その操作を行えるか (§6.4.4「判定」「取得できないとき」)。
 *
 * 判定は「残量 >= その操作の必要なポイント」で、**ちょうど同じならできる**
 * (サーバの判定「使用量 + 必要なポイント > 上限なら拒否」と同じ境界)。
 * 最終の判定はサーバで、画面の判定は目安 (残量はテナント全体で 1 つのため、
 * 他の利用者の消費は取り直すまで画面に入らない)。
 *
 * 取得できないとき:
 *   ① 残量が無い (最初の取得が済む前・最初の取得に失敗した) → 押せる (Issue #882 より前と同じ)
 *   ② 残量はあるが `required_points` かそのキーが無い (BE が Issue #882 より前の版)
 *      → その操作は「残量 > 0」で判定する (Issue #882 より前の判定)
 *   ③ 取り直しの間・取り直しに失敗したときは、前に取れた値で判定する
 *      (呼出側が取り直しの失敗で quota を消さないことによる。useAiMatching.js の loadQuota)
 *
 * @param {{remaining_points?: number, required_points?: Object.<string, number>}|null|undefined} quota
 *   残量 API の応答 (未取得・取得失敗のときは null)
 * @param {string} operation QUOTA_OPERATION のいずれか
 * @returns {boolean} 操作できるとき true
 */
export function hasEnoughPoints(quota, operation) {
    if (!quota) return true; // ①
    const requiredPoints = quota.required_points?.[operation];
    if (typeof requiredPoints !== 'number') return quota.remaining_points > 0; // ②
    return quota.remaining_points >= requiredPoints;
}
